import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonStandingsComponent } from '@/app/components/shared/skeleton';
import { TranslationService } from '@/app/services/translation.service';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ITeamStanding } from '../../models';
import { CompetitionStandingsService } from '../../services';

@Component({
  selector: 'app-standings',
  standalone: true,
  imports: [
    CommonModule,
    NzGridModule,
    RouterLink,
    TranslateModule,
    NzTableModule,
    NzToolTipModule,

    SkeletonStandingsComponent,
    NoResultComponent,
  ],
  templateUrl: './standings.component.html',
  styleUrl: 'standings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandingsComponent {
  private readonly translateService = inject(TranslateService);
  private fCompetitionService = inject(CompetitionStandingsService);

  translationService = inject(TranslationService);

  competitionId = input.required<string>();

  fetchComputed = computed(() => ({
    isLoading: this.fCompetitionService.isLoadingCompetitionsStandings(),
    isError: this.fCompetitionService.isErrorCompetitionsStandings(),
  }));

  competitionStandings = computed(() =>
    this.fCompetitionService.competitionsStandings()
  );

  urlSVG = environment.urlIcon;
  urlIMG = environment.urlIMG;

  listOfColumn = [
    {
      title: '#',
      compare: (a: ITeamStanding, b: ITeamStanding) => a.Position - b.Position,
      priority: 1,
      show: true,
      tooltip: 'Position',
      fixed: true,
      width: '15px',
      align: 0,
    },
    {
      title: 'Team',
      compare: (a: ITeamStanding, b: ITeamStanding) =>
        this.translationService
          .getTraduccionName(a.NameTranslation, a.Name)
          .localeCompare(
            this.translationService.getTraduccionName(b.NameTranslation, b.Name)
          ),
      priority: 2,
      show: true,
      tooltip: 'Team',
      fixed: true,
      width: '80px',
      align: 0,
    },
    {
      title: 'GP',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.Played === b.Played) {
          return a.Position - b.Position;
        }

        return b.Played - a.Played;
      },
      priority: 2,
      show: true,
      tooltip: 'Matches',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'W',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.Won === b.Won) {
          return a.Position - b.Position;
        }

        return b.Won - a.Won;
      },
      priority: 2,
      show: true,
      tooltip: 'Win',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'T',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.Draw === b.Draw) {
          return a.Position - b.Position;
        }
        return b.Draw - a.Draw;
      },
      priority: 2,
      show: true,
      tooltip: 'Tied',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'L',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.Loss === b.Loss) {
          return a.Position - b.Position;
        }
        return b.Loss - a.Loss;
      },
      priority: 2,
      show: true,
      tooltip: 'Loss',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'GD',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.GoalsDifference === b.GoalsDifference) {
          return a.Position - b.Position;
        }
        return b.GoalsDifference - a.GoalsDifference;
      },
      priority: 2,
      show: true,
      tooltip: 'Diff',
      fixed: false,
      width: '24px',
      align: 2,
    },
    {
      title: 'PTS',
      compare: (a: ITeamStanding, b: ITeamStanding) => {
        if (a.Points === b.Points) {
          return a.Position - b.Position;
        }
        return b.Points - a.Points;
      },
      priority: 2,
      show: true,
      tooltip: 'Points',
      fixed: false,
      width: '30px',
      align: 2,
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.fCompetitionService.getCompetitionStandings(this.competitionId());
  }

  getTeamStandings() {
    return this.competitionStandings()?.StageStandings ?? [];
  }

  getPromotions() {
    return this.competitionStandings()?.Promotions ?? [];
  }

  getNameTranslation(
    translations: Record<string, string> | null,
    defaultName: string
  ) {
    return this.translationService.getTraduccionName(translations, defaultName);
  }
}
